import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { leadApi } from '../services/api';
import { TagsSummary, TagDimension, TagValueCount } from '../types';
import { label } from '../lib';

// Display copy distilled from the vocabulary's playbook lines
// (services/agent-service/knowledge/lead_tags.json). Portal-local: display
// concern only — core stays vocabulary-free.
const DIMENSION_HINTS: Record<string, string> = {
  manual: 'Operator-authored tags — never touched by auto-tagging.',
  language: 'Route to an operator fluent in their language before any visit ask.',
  lead_type: 'Only genuine buyers get cadence; partners → partnerships desk, vendors exit.',
  buyer_type: 'Picks the visit pitch: investment math vs livability vs weekend experience.',
  timeline: 'Sets cadence intensity; time the push to any stated release event.',
  disposition: 'The kill-switch: these exit active cadence; do_not_contact is hard-stop.',
  visit_status: 'THE pipeline dimension — committed needs a day-before confirmation call.',
  visit_logistics: 'Match the ask to their constraint: weekend slots, return dates, pickup.',
  objection: 'What the visit pitch must pre-empt — sell the drive, carry rate evidence.',
  finance: 'Arrive at the visit ask with the money answer ready (loan / staggered plan).',
  decision_process: 'Get the whole decision unit on site in ONE visit (weekend family slot).',
  product_requirement: 'Fix pitch mismatch: plot-only → appreciation angle; style → model villa.',
  reachability: 'Stops wasted dials: screening/voicemail leads flip to WhatsApp-first.',
  contact_protocol: 'The lead told us HOW to reach them — off-window calls are bugs.',
  origin: 'Drives visit logistics: area pickup, or bundle into their next Hyderabad trip.',
  geo_pref: 'Hard preference for another corridor = pitch mismatch — deprioritize/cross-sell.',
  competitor: 'Comparison shoppers are won by whoever lands the physical visit first.',
  profession: 'Calibrates pitch depth; real-estate insiders see through scripts.',
  source: 'Warm conversational hook + closes the loop on which creative produces visits.',
};

// visit_status funnel order (toward a done visit) + sequential ramp of
// forest, light→dark by progress. `declined` is off-funnel → gray.
const FUNNEL_ORDER = [
  'not_discussed', 'interested_no_date', 'serial_rescheduler',
  'committed_no_show', 'committed', 'done',
];
// Sage → forest by progress toward a conducted visit. Two states sit outside
// the ramp on purpose: a no-show is a leak, not a step (brick), and `declined`
// is off-funnel entirely (neutral).
const FUNNEL_COLORS: Record<string, string> = {
  not_discussed: '#E4EDE3',
  interested_no_date: '#C4D9C1',
  serial_rescheduler: '#A8C4A4',
  committed_no_show: '#A94435',
  committed: '#5B8A60',
  done: '#355E3B',
  declined: '#B5B0A8',
};

/** Explore leads by tags — the live tag distribution; every number opens the
 *  leads behind it. Lives on the Dashboard (the Nurture app's Explore card). */
const TagExplorer: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<TagsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [expandedDims, setExpandedDims] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    leadApi.tagsSummary(includeExcluded)
      .then(s => { if (!cancelled) setSummary(s); })
      .catch(err => console.error('tags summary failed', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [includeExcluded]);

  const openTag = (dimension: string, v: TagValueCount) => {
    if (v.count === 1 && v.lead_id) {
      navigate(`/leads/${v.lead_id}`);
    } else {
      navigate(`/leads?tag=${encodeURIComponent(`${dimension}:${v.value}`)}`);
    }
  };

  const visitStatus = summary?.dimensions.find(d => d.dimension === 'visit_status');

  // Tag cloud: top 60 values across all dimensions, font size ∝ sqrt(count).
  const cloud = useMemo(() => {
    if (!summary) return [];
    const all = summary.dimensions.flatMap(d =>
      d.values.map(v => ({ dimension: d.dimension, ...v })));
    all.sort((a, b) => b.count - a.count);
    const top = all.slice(0, 60);
    const max = top[0]?.count ?? 1;
    const min = top[top.length - 1]?.count ?? 1;
    return top.map(t => ({
      ...t,
      px: 12 + 16 * Math.sqrt((t.count - min) / Math.max(1, max - min)),
    }));
  }, [summary]);

  return (
    <section>
      <div className="page-head !mb-3">
        <div>
          <h2 className="card-h !mb-0">Explore leads by tags</h2>
          <p className="page-sub">Live call-derived tag distribution — every number opens the leads behind it</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite cursor-pointer">
          <input
            type="checkbox"
            checked={includeExcluded}
            onChange={e => setIncludeExcluded(e.target.checked)}
            className="accent-forest"
          />
          Include do-not-contact leads
        </label>
      </div>

      <div>
        {loading || !summary ? (
          <div className="flex items-center justify-center h-64 text-graphite">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tag summary…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stat tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile label="Active leads" value={summary.total_active} />
              <StatTile
                label="Tagged"
                value={summary.tagged}
                onClick={() => navigate('/leads?has_tags=true')}
              />
              <StatTile
                label="Untagged"
                value={summary.untagged}
                onClick={() => navigate('/leads?has_tags=false')}
              />
              <StatTile
                label="Coverage"
                value={summary.total_active > 0
                  ? `${Math.round((summary.tagged / summary.total_active) * 100)}%`
                  : '—'}
              />
            </div>

            {/* Site-visit funnel */}
            {visitStatus && (
              <Panel title="Site-visit funnel" hint={DIMENSION_HINTS.visit_status}>
                <FunnelStrip dim={visitStatus} onOpen={openTag} />
              </Panel>
            )}

            {/* Tag cloud */}
            {cloud.length > 0 && (
              <Panel title="Tag cloud" hint="Top tags across every dimension — size = lead count.">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5 leading-tight">
                  {cloud.map(t => (
                    <button
                      key={`${t.dimension}:${t.value}`}
                      onClick={() => openTag(t.dimension, t)}
                      title={`${t.dimension}: ${label(t.value)} — ${t.count} lead${t.count === 1 ? '' : 's'}`}
                      className={`hover:underline ${
                        t.dimension === 'manual'
                          ? 'text-amber-600 hover:text-amber-700'
                          : 'text-charcoal hover:text-forest'
                      }`}
                      style={{ fontSize: `${t.px}px` }}
                    >
                      {label(t.value)}
                    </button>
                  ))}
                </div>
              </Panel>
            )}

            {/* Dimension grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {summary.dimensions
                .filter(d => d.dimension !== 'visit_status')
                .map(d => (
                  <DimensionCard
                    key={d.dimension}
                    dim={d}
                    expanded={expandedDims.has(d.dimension)}
                    onToggleExpand={() =>
                      setExpandedDims(prev => {
                        const next = new Set(prev);
                        if (next.has(d.dimension)) next.delete(d.dimension);
                        else next.add(d.dimension);
                        return next;
                      })}
                    onOpen={openTag}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const StatTile: React.FC<{ label: string; value: number | string; onClick?: () => void }> =
  ({ label: l, value, onClick }) => (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`bg-warm-white border border-sketch rounded-xl shadow-sketch p-4 text-left ${
        onClick ? 'hover:border-forest/50 hover:shadow transition-all cursor-pointer' : 'cursor-default'
      }`}
    >
      <p className="text-graphite text-xs uppercase tracking-wide">{l}</p>
      <p className="font-display text-3xl text-charcoal mt-1">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </button>
  );

const Panel: React.FC<{ title: string; hint?: string; children: React.ReactNode }> =
  ({ title, hint, children }) => (
    <div className="bg-warm-white border border-sketch rounded-xl shadow-sketch p-5">
      <h3 className="text-charcoal font-medium">{title}</h3>
      {hint && <p className="text-graphite text-xs mt-0.5 mb-4">{hint}</p>}
      {children}
    </div>
  );

/** visit_status as one horizontal segmented strip, ordered by progress toward
 *  a done visit (sequential light→dark; `declined` off-funnel in gray). */
const FunnelStrip: React.FC<{ dim: TagDimension; onOpen: (d: string, v: TagValueCount) => void }> =
  ({ dim, onOpen }) => {
    const byValue = new Map(dim.values.map(v => [v.value, v]));
    const ordered = [
      ...FUNNEL_ORDER.filter(v => byValue.has(v)),
      ...dim.values.map(v => v.value).filter(v => !FUNNEL_ORDER.includes(v)),
    ];
    const total = dim.total || 1;
    return (
      <div>
        <div className="flex w-full h-9 gap-0.5 rounded overflow-hidden">
          {ordered.map(value => {
            const v = byValue.get(value)!;
            const pct = (v.count / total) * 100;
            return (
              <button
                key={value}
                onClick={() => onOpen(dim.dimension, v)}
                title={`${label(value)} — ${v.count} lead${v.count === 1 ? '' : 's'}`}
                className="h-full hover:opacity-80 transition-opacity ring-1 ring-black/5"
                style={{
                  width: `${pct}%`,
                  minWidth: '6px',
                  backgroundColor: FUNNEL_COLORS[value] ?? '#9ca3af',
                }}
              />
            );
          })}
        </div>
        {/* Legend row — identity + counts live here, never color-alone */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
          {ordered.map(value => {
            const v = byValue.get(value)!;
            return (
              <button
                key={value}
                onClick={() => onOpen(dim.dimension, v)}
                className="flex items-center gap-1.5 text-xs text-graphite hover:text-charcoal"
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm inline-block ring-1 ring-black/10"
                  style={{ backgroundColor: FUNNEL_COLORS[value] ?? '#9ca3af' }}
                />
                {label(value)}
                <span className="font-medium text-charcoal">{v.count}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

const TOP_N = 8;

const DimensionCard: React.FC<{
  dim: TagDimension;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpen: (d: string, v: TagValueCount) => void;
}> = ({ dim, expanded, onToggleExpand, onOpen }) => {
  const max = dim.values[0]?.count ?? 1;
  const shown = expanded ? dim.values : dim.values.slice(0, TOP_N);
  const hidden = dim.values.length - TOP_N;
  const manual = dim.dimension === 'manual';
  return (
    <div className="bg-warm-white border border-sketch rounded-xl shadow-sketch p-5">
      <div className="flex items-baseline justify-between">
        <h3 className="text-charcoal font-medium capitalize">{label(dim.dimension)}</h3>
        <span className="text-xs text-graphite">{dim.total} tags</span>
      </div>
      {DIMENSION_HINTS[dim.dimension] && (
        <p className="text-graphite text-xs mt-0.5">{DIMENSION_HINTS[dim.dimension]}</p>
      )}
      <div className="mt-3 space-y-1">
        {shown.map(v => (
          <button
            key={v.value}
            onClick={() => onOpen(dim.dimension, v)}
            className="w-full flex items-center gap-2 group text-left"
            title={`${v.count} lead${v.count === 1 ? '' : 's'}`}
          >
            <span className="w-44 shrink-0 truncate text-sm text-charcoal group-hover:text-forest">
              {label(v.value)}
            </span>
            <span className="flex-1 h-4 relative">
              <span
                className={`absolute inset-y-0 left-0 rounded-r ${manual ? 'bg-amber-400/80' : 'bg-forest/75'} group-hover:opacity-90`}
                style={{ width: `${Math.max(2, (v.count / max) * 100)}%` }}
              />
            </span>
            <span className="w-10 shrink-0 text-right text-xs font-medium text-charcoal tabular-nums">
              {v.count}
            </span>
          </button>
        ))}
      </div>
      {hidden > 0 && (
        <button
          onClick={onToggleExpand}
          className="mt-2 text-xs text-forest hover:underline"
        >
          {expanded ? 'Show less' : `Show all ${dim.values.length}`}
        </button>
      )}
    </div>
  );
};

export default TagExplorer;
